import { Location } from '@cobliteam/common-types';
import { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import useNavigationHistoryStore from '../store/useNavigationHistoryStore';
import useOverlayMediaStore from '../store/useOverlayMediaStore';

const toSnakeCase = (str: string) => str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

const useOnNavigate = () => {
  const history = useHistory();
  const setOverlayMediaId = useOverlayMediaStore((s) => s.setMediaId);
  const clearOverlay = useOverlayMediaStore((s) => s.clear);
  const markNavigated = useNavigationHistoryStore((s) => s.markNavigated);

  useEffect(() => {
    return history.listen((_location, action) => {
      clearOverlay();
      // Only PUSH adds a new in-app entry. REPLACE keeps the stack depth
      // (auth redirects, URL normalizations, the onNavigateBack fallback),
      // and POP just walks an existing entry.
      if (action === 'PUSH') {
        markNavigated();
      }
    });
  }, [history, clearOverlay, markNavigated]);

  return useCallback(
    (path: string, params?: Record<string, unknown>, options?: { target?: string }) => {
      // Intercepts CAMERA_EVENTS location to display a DialogMedia instead
      // of navigating to a new page
      if (path === Location.CAMERA_EVENTS && params?.mediaId) {
        setOverlayMediaId(String(params.mediaId));
        return;
      }

      const isExternalTarget = options?.target === '_blank';

      const searchParams = new URLSearchParams();
      if (params) {
        for (const [key, value] of Object.entries(params)) {
          if (value !== undefined && value !== null) {
            const paramKey = isExternalTarget ? toSnakeCase(key) : key;
            searchParams.append(paramKey, String(value));
          }
        }
      }

      const qs = searchParams.toString();
      const fullPath = qs ? `${path}?${qs}` : path;

      if (isExternalTarget) {
        window.open(fullPath, '_blank', 'noopener,noreferrer');
      } else {
        history.push(fullPath);
      }
    },
    [history, setOverlayMediaId]
  );
};

export default useOnNavigate;
